/**
 * @fileoverview Converts between the schema TableSchemaBuilder edits and the
 * `details` rows zb_reporting_questions stores for tabular and Yes/No KPIs.
 * Shared by the question form and the KPI grid so both write the same rows.
 */

const columnDetail = (id, optionType, column) => ({
  detail_id: id,
  option_type: optionType,
  option: column.label,
  unit_id: column.unit_id,
  data_type: column.data_type,
  dropdown_options: column.dropdown_options,
  rules: null,
});

const fixedRowsAndSameColumns = (schema) => {
  let id = 1;
  return [
    ...(schema.rows || []).map((row) => ({ detail_id: id++, option_type: "row", option: row.label, rules: null })),
    ...(schema.columns || []).map((column) => columnDetail(id++, "column", column)),
  ];
};

const fixedRowsAndDifferentColumns = (schema) => {
  let id = 1;
  const result = [];
  const globalColumns = schema.pre_defined_columns || [];
  (schema.rows || []).forEach((row, ri) => {
    result.push({ detail_id: id++, option_type: "row", option: row.label, rules: null });
    globalColumns.forEach((column) => result.push(columnDetail(id++, `column${ri + 1}`, column)));
    (row.columns || []).forEach((column) => result.push(columnDetail(id++, `column${ri + 1}`, column)));
  });
  return result;
};

const predefinedRowsOf = (schema) =>
  schema.pre_defined_rows.map((row, idx) => ({ detail_id: idx + 1, option_type: "row", option: row.label, rules: null }));

const dynamicRows = (schema) => {
  let id = 1;
  const rowEntry = { detail_id: id++, option_type: "row", option: "1", rules: null };
  if (schema?.pre_defined_rows?.length > 0) rowEntry.pre_defined_rows = predefinedRowsOf(schema);
  return [rowEntry, ...(schema.columns || []).map((column) => columnDetail(id++, "column", column))];
};

const fullyDynamic = (schema) => {
  const rowEntry = { detail_id: 1, option_type: "row", option: "1", rules: null };
  if (schema?.pre_defined_rows?.length > 0) rowEntry.pre_defined_rows = predefinedRowsOf(schema);
  const columnEntry = { detail_id: 2, option_type: "column", option: "1", rules: null };
  if (schema?.pre_defined_columns?.length > 0) {
    columnEntry.pre_defined_columns = schema.pre_defined_columns.map((column, idx) => ({
      detail_id: idx + 1,
      option_type: "column",
      option: column.label,
      data_type: column.data_type,
      dropdown_options: column.dropdown_options,
      rules: null,
    }));
  }
  return [rowEntry, columnEntry];
};

const yesNo = (schema) => {
  if (!schema.row) return [];
  let id = 1;
  return [
    { detail_id: id++, option_type: "attibutes", option: schema.row.label, rules: null },
    ...(schema.columns || []).map((column) => ({
      detail_id: id,
      option_type: `Q${id++}`,
      option: column.label,
      data_type: column.data_type,
      dropdown_options: column.dropdown_options,
      rules: null,
    })),
  ];
};

/**
 * @param {object} schema as TableSchemaBuilder reports it
 * @returns {object[]|null} the details rows, or null for a schema it cannot place
 */
export const schemaToDetails = (schema) => {
  if (schema?.type === "yes_no") return yesNo(schema);
  if (schema?.type === "fully-dynamic") return fullyDynamic(schema);
  if (schema?.rows && schema?.columns) return fixedRowsAndSameColumns(schema);
  if (schema?.rows) return fixedRowsAndDifferentColumns(schema);
  if (schema?.columns) return dynamicRows(schema);
  return null;
};

const columnOf = (detail) => {
  const column = { label: detail.option, data_type: detail.data_type, dropdown_options: detail.dropdown_options };
  if (detail.unit_id) column.unit_id = detail.unit_id;
  return column;
};

const yesNoSchema = (details) => {
  const rowLabel = details.find((d) => d.option_type === "attibutes")?.option ?? "Response";
  const columns = details
    .filter((d) => d.option_type.startsWith("Q"))
    .map((d) => ({ label: d.option, data_type: d.data_type, dropdown_options: d.dropdown_options }));
  return { type: "yes_no", mode: "single-row-multiple-columns", row: { label: rowLabel }, columns };
};

const tabularSchema = (details) => {
  const rowDetails = details.filter((d) => d.option_type.startsWith("row"));
  const columnDetails = details.filter((d) => d.option_type.startsWith("column"));
  const isDynamic = rowDetails.length === 1 && rowDetails[0].option === "1";
  const isFullyDynamic = isDynamic && columnDetails.length === 1 && columnDetails[0].option === "1";
  const rowSentinel = rowDetails.find((r) => r.option === "1");
  const predefinedRows = rowSentinel?.pre_defined_rows?.length > 0
    ? rowSentinel.pre_defined_rows.map((r) => ({ label: r.option }))
    : null;

  if (isFullyDynamic) {
    const schema = { type: "fully-dynamic" };
    if (predefinedRows) schema.pre_defined_rows = predefinedRows;
    const columnSentinel = columnDetails.find((c) => c.option === "1");
    if (columnSentinel?.pre_defined_columns?.length > 0) {
      schema.pre_defined_columns = columnSentinel.pre_defined_columns.map((c) => ({
        label: c.option,
        data_type: c.data_type,
        dropdown_options: c.dropdown_options,
      }));
    }
    return schema;
  }
  if (isDynamic) {
    const schema = { type: "dynamic", columns: columnDetails.map(columnOf) };
    if (predefinedRows) schema.pre_defined_rows = predefinedRows;
    return schema;
  }
  const columnOptionTypes = new Set(columnDetails.map((c) => c.option_type));
  const sameColumns = columnOptionTypes.size === 1 && columnOptionTypes.has("column");
  if (sameColumns) {
    return {
      type: "fixed",
      mode: "same-columns",
      rows: rowDetails.map((r) => ({ label: r.option })),
      columns: columnDetails.map(columnOf),
    };
  }
  return {
    type: "fixed",
    mode: "different-columns",
    rows: rowDetails.map((r, ri) => ({
      label: r.option,
      columns: columnDetails.filter((c) => c.option_type === `column${ri + 1}`).map(columnOf),
    })),
  };
};

/**
 * @param {string} questionType "yes_no" or "tabular_question"
 * @param {object[]} details in stored order
 * @returns {object|object[]} the schema TableSchemaBuilder starts from, or an empty list when there is none
 */
export const detailsToSchema = (questionType, details) => {
  if (!Array.isArray(details) || details.length === 0) return [];
  if (questionType === "yes_no") return yesNoSchema(details);
  if (questionType === "tabular_question") return tabularSchema(details);
  return [];
};
